import classNames from 'clsx';
import { xray } from 'lib/xray';
import React, { memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { CONTENT_LOAD_TIMEOUT } from 'reactApp/ui/ReactViewer/ReactViewer.constants';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { getAttachType } from 'reactApp/utils/downloadDocumentHelpers';
import { sendLoadTime } from 'reactApp/utils/sendLoadTime';

import { useConvertDocumentToPdf } from './hooks/useConvertDocumentToPdf';
import styles from './ViewerPdf.css';

export interface ViewerPdfFallback {
    radarError?: string;
}

export interface ViewerPdfProps {
    url: string;
    renderError?: (fallbackData: ViewerPdfFallback) => ReactElement | null;
    fillParent?: boolean;
    file?: CloudFile;
    converterStorage?: EStorageType;
    convert?: boolean;
    radarError?: string;
    onError?: () => void;
    onOpen?: () => void;
    onConvertSuccess?: (url: string) => void;
}

export const ViewerPdf = memo<ViewerPdfProps>(
    ({ url, file, converterStorage, convert, onError, onOpen, renderError = () => null, onConvertSuccess, fillParent }) => {
        const ref = useRef<number | undefined>();
        const mountTime = useRef<number>(0);

        const { fileURL, error } = useConvertDocumentToPdf({ file, convert, converterStorage });

        const [isError, setError] = useState(false);
        const [fallbackData, setFallbackData] = useState<ViewerPdfFallback>({});
        const [isLoaded, setLoaded] = useState(false);
        const attachType = useMemo(() => getAttachType(file), [file]);

        const src = convert ? fileURL : url;
        const showSpinner = !isLoaded || (convert && !src);

        const handleError = useCallback(() => {
            onError?.();
            setFallbackData({ radarError: 'client' });
            setError(true);
            setLoaded(true);
        }, [setError, setLoaded, onError]);

        useEffect(() => {
            if (convert && fileURL) {
                onConvertSuccess?.(fileURL);
            }
        }, [convert, fileURL]);

        useEffect(() => {
            mountTime.current = Date.now();
        }, []);

        useEffect(() => {
            if (error) {
                setError(!!error);
                setFallbackData({ radarError: `api_${error.radarName || error.status || error.message}` });
            }
        }, [error]);

        const handleLoad = useCallback(() => {
            if (ref.current) {
                clearTimeout(ref.current);
            }

            if (!isLoaded) {
                sendLoadTime(mountTime.current, `viewer_pdf${convert ? '_ovidius' : ''}${attachType ? `_${attachType}` : ''}_load_time`);
            }

            onOpen?.();
            setLoaded(true);
        }, [ref.current, setLoaded, onOpen]);

        useEffect(() => {
            if (ref.current) {
                clearTimeout(ref.current);
            }

            ref.current = window.setTimeout(handleError, CONTENT_LOAD_TIMEOUT);

            return () => {
                if (ref.current) {
                    clearTimeout(ref.current);
                }
            };
        }, [url, handleError]);

        useEffect(() => {
            if (isError) {
                xray.send(`viewer_pdf_err${convert ? '_ovidius' : ''}${attachType ? `_${attachType}` : ''}`);
            }
        }, [isError]);

        if (isError) {
            return renderError(fallbackData);
        }

        return (
            <div
                className={classNames(styles.root, {
                    [styles.root_fillParent]: fillParent,
                })}
            >
                <iframe
                    src={src}
                    className={styles.pdf}
                    allowFullScreen
                    allow="autoplay; fullscreen"
                    frameBorder={0}
                    onError={handleError}
                    onLoad={handleLoad}
                />
                {showSpinner && <Spinner />}
            </div>
        );
    }
);

ViewerPdf.displayName = 'ViewerPdf';

ViewerPdf.whyDidYouRender = true;
